/**
 * Instead of the browser's own "please fill in" bubble, which can sit far from the field (or under the top bar) when
 * the field is out of view: scroll the first invalid field to the middle of the screen, focus it, mark it, and give a
 * message naming it, e.g. "Component (category): Please select an item in the list."
 *
 *   const invalid = useInvalidField();
 *   <form onInvalidCapture={invalid.onInvalidCapture} onSubmit={() => { invalid.clear(); ... }}>
 *   {invalid.message && <div className="banner error">{invalid.message}</div>}
 */
import { useRef, useState, type FormEvent } from 'react';

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** The visible name of a field: its .field-label (without the "*"), else aria-label or placeholder. */
function fieldName(el: Control): string {
  const label = el.closest('.field')?.querySelector('.field-label')?.textContent
    ?? (el.id ? document.querySelector(`label[for="${CSS.escape(el.id)}"]`)?.textContent : null)
    ?? el.getAttribute('aria-label') ?? el.getAttribute('placeholder') ?? '';
  return label.replace(/\*/g, '').trim();
}

export function useInvalidField() {
  const [message, setMessage] = useState<string | null>(null);
  const handled = useRef(false);
  const onInvalidCapture = (e: FormEvent<HTMLFormElement>) => {
    const el = e.target as Control;
    e.preventDefault(); // no browser bubble
    el.classList.add('is-invalid');
    const unmark = () => el.classList.remove('is-invalid');
    el.addEventListener('input', unmark, { once: true });
    el.addEventListener('change', unmark, { once: true });
    // one submit fires "invalid" for every invalid field, in order: only the first one is shown
    if (handled.current) return;
    handled.current = true;
    requestAnimationFrame(() => (handled.current = false));
    const name = fieldName(el);
    setMessage(name ? `${name}: ${el.validationMessage}` : el.validationMessage);
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus({ preventScroll: true });
  };
  return { message, clear: () => setMessage(null), onInvalidCapture };
}
